import { useMemo } from "react";

import { useQuery, type UseQueryResult } from "@tanstack/react-query";

import { getModalities, getModalityByAbbr } from "@/apis/modalities";
import { generateModalitiesChartConfig } from "@/features/dashboard/constants/modalities-chart-config";
import type { ModalitiesResponse, Modality } from "@/shared/schemas/modalities";

// Query keys para modalidades
export const modalitiesKeys = {
  all: ["modalities"] as const,
  list: () => [...modalitiesKeys.all, "list"] as const,
  byAbbr: (abbr: string) => [...modalitiesKeys.all, "byAbbr", abbr] as const,
} as const;

/**
 * Hook para obtener todas las modalidades
 * @returns UseQueryResult<ModalitiesResponse, Error>
 */
export function useModalities(): UseQueryResult<ModalitiesResponse, Error> {
  return useQuery({
    queryKey: modalitiesKeys.list(),
    queryFn: getModalities,
    staleTime: 1000 * 60 * 30, // 30 minutos - las modalidades no cambian frecuentemente
    gcTime: 1000 * 60 * 60, // 1 hora en cache
    retry: 3,
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
  });
}

/**
 * Hook para obtener una modalidad específica por su abreviación
 * @param abbr - La abreviación de la modalidad
 * @returns UseQueryResult<Modality | null, Error>
 */
export function useModalityByAbbr(abbr: string): UseQueryResult<Modality | null, Error> {
  return useQuery({
    queryKey: modalitiesKeys.byAbbr(abbr),
    queryFn: () => getModalityByAbbr(abbr),
    enabled: !!abbr, // Solo ejecutar si hay una abreviación
    staleTime: 1000 * 60 * 30, // 30 minutos
    gcTime: 1000 * 60 * 60, // 1 hora en cache
    retry: 3,
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
  });
}

/**
 * Hook personalizado que combina la lista de modalidades con utilidades
 * @returns Objeto con modalidades y funciones de utilidad
 */
export function useModalitiesWithUtils() {
  const { data: modalities, ...queryResult } = useModalities();

  return {
    modalities: modalities || [],
    ...queryResult,
  };
}

/**
 * Hook personalizado que combina modalidades con configuración de chart
 * @param language - Idioma para los nombres de modalidades ('en' | 'es')
 * @returns Objeto con modalidades, configuración de chart y estado de carga
 */
export function useModalitiesChart(language: "en" | "es" = "es") {
  const { data: modalities, isLoading, error, ...queryResult } = useModalities();

  // Generar configuración de chart basada en modalidades
  const chartConfig = useMemo(() => {
    if (!modalities) return {};
    return generateModalitiesChartConfig(modalities, language);
  }, [modalities, language]);

  return {
    modalities: modalities || [],
    chartConfig,
    isLoading,
    error,
    ...queryResult,
  };
}
